<template>
    <div class="calculator">
        <div v-if="this.interest">
            <form @submit.prevent="done">
								<div
									v-if="token && leasingCalculatorEnabled"
									class="flex flex-column-sm gap-40"
								>
									<div class="form-group">
										<label>סוג מחשבון</label>
                    <radio-group
                        v-model="calculatorType"
                        name="calculator_type"
                        :options="calculatorTypeOptions"
                    ></radio-group>
									</div>
								</div>
                <div class="flex flex-column-sm">
                    <div>
                        <h2 class="title"><span v-if="!titleText">פשוט לחשב את המימון שלך</span><span v-else>{{titleText}}</span></h2>
                        <div class="bg-section mb-20" :class="{section: !!token}">
                            <div class="flex gap-30 mb-20 flex-nowrap">
                                <div v-if="isFunding" class="form-group half-mobile" v-show="!hideYear">
                                    <label for="car_year">שנת ייצור</label>
                                    <select
                                        class="pm-input pm-input-100-w"
                                        id="car_year"
                                        name="car_year"
                                        v-model="calculator.car_year"
                                        @change="carYearChanged"
                                    >
                                        <option v-for="year in carYears" :key="year" :value="year">{{year}}</option>
                                    </select>
                                </div>
                                <div class="form-group half-mobile">
                                    <label for="car_cost">עלות (₪)</label>
                                    <input
                                        class="pm-input pm-input-100-w"
                                        type="number"
                                        step="500"
                                        id="car_cost"
                                        name="car_cost"
                                        v-model="calculator.car_cost"
                                        @click="carCostChanged"
                                    >
                                </div>
                            </div>
                            <div class="flex gap-30 mb-20">
                                <div v-show="!hideType" class="form-group">
                                    <label>קטגוריית רכב</label>
                                    <radio-group
                                        v-model="calculator.car_type"
                                        name="car_type"
                                        :options="carTypesOptions"
                                    ></radio-group>
                                </div>
                            </div>
                            <div class="form-group">
                                <label v-if="!monthsLabel">פריסת ההלוואה (חודשים) </label>
                                <label v-else>{{monthsLabel}}</label>
                                <select
                                    class="pm-input pm-input-100-w"
                                >
                                    <option v-for="payment in availablePayments" :key="payment.value" :value="payment.value" :disabled="payment.disabled">
                                      {{payment.value}} ({{yearsFromMonths(payment.value)}})
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="section">
                            <div class="form-group">
                                <label v-if="!imprestLabel">מקדמה (₪)</label>
                                <label v-else>{{imprestLabel}}</label>
                                <div class="range-wrap">
                                    <div class="pm-input price-view"><span>{{ numberFormat(calculator.imprest) }}</span></div>
                                    <slider
                                        class="range-slider"
                                        :tooltips="true"
                                        :min="minImprest"
                                        :lazy="false"
                                        :step="500"
                                        :max="maxImprest"
                                        v-model.lazy="sliderImprest"
                                        @change="sliderImprestChanged"
                                        direction="rtl"
                                    ></slider>
                                </div>
                            </div>
                        </div>
                        <div class="section" v-if="bulletloanEnabled">
                            <div class="form-group">
                                <label v-if="!bulletLoanLabel">תשלום בסוף התקופה (₪)</label>
                                <label v-else>{{bulletLoanLabel}}</label>
                                <div class="range-wrap">
                                    <div class="pm-input price-view"><span>{{ numberFormat(calculator.bullet_loan_amount) }}</span></div>
                                    <slider
                                        class="range-slider"
                                        :tooltips="true"
                                        :min="minBulletLoan"
                                        :lazy="false"
                                        :step="500"
                                        :max="maxBulletLoanAmount"
                                        v-model.lazy="sliderBulletLoanAmount"
                                        direction="rtl"
                                        @change="sliderBulletLoanAmountChanged"
                                    ></slider>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="monthly-payment-wrapper flex flex-column">
                        <img class="image-mobile" src="@/assets/images/design-image-mobile.png" alt="מחשבון מימון לרכב">
                        <img class="image-desktop" src="@/assets/images/design-image-desktop.png" alt="מחשבון מימון לרכב">
                        <div class="monthly-payment" v-if="interest">
                            <div class="pm-input price-view"><span>{{ numberFormat(monthlyPayment) }}</span> ₪</div>
                            <span v-if="!monthlyPaymentLabel">החזר חודשי</span>
                            <span v-else>{{monthlyPaymentLabel}}</span>
                            <slider class="range-slider"
                                    :tooltips="false"
                                    :min="minMonthlyPayment"
                                    :lazy="true"
                                    :step="1"
                                    :max="maxMonthlyPayment"
                                    :modelValue="monthlyPayment"
                                    direction="rtl"
                                    @input="sliderMonthlyPaymentChanged"
                            ></slider>
                            <div class="cta-container" v-if="cta.text">
                                <a class="cta-btn" :href="ctaUrl" :target="cta.target" @click="done">{{cta.text}}</a>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
        <div v-else>
            <p>טוען...</p>
        </div>
    </div>
</template>
<script>
import {mapActions, mapGetters, mapState} from "vuex"
import numeral from 'numeral'
import Slider from '@vueform/slider'
import xirr from "xirr";
import moment from "moment";
import RadioGroup from "@/components/RadioGroup.vue";

export default {
    name: "Calculator",
    components: {RadioGroup, Slider},
    data() {
        return {
            stage: 1,
            minimum_loan: 30000,
            preferred_loan: 'popular',
            currentYear: new Date().getFullYear(),
            sliderImprest: 0,
            sliderBulletLoanAmount: 0,
            sliderMonthlyPayment: 0,
            monthlyPayment: 0,
            monthlyPaymentSelfChange: true,
            prePaymentDaysInterest: 0, // הריבית השנתית חלקי 365 כפול מספר הימים בין היום שנתנו את ההלוואה ליום שהלקוח שילם תשלום ראשון
            benchmark: 0.13,
            primeInterest: 0.045,
            primePlus: -0.02,
            openingCommissionPercent: 0.0145,
            openingCommissionConstant: 400,
            collectionFee: 4.9,
            releaseOfEncumbrance: 149,
            extendingEncumbrance: 35,
            profOfInsurance: 69,
            openingCommissionDivider: 3,
            minMonthlyPayment: 0,
            maxMonthlyPayment: 0
        }
    },
    computed: {
        fundingSum(){
          return this.calculator.car_cost - this.calculator.imprest;
        },
        xirrTest(){
          return this.xirrPayments > this.benchmark
        },
        openingCommission(){
          return this.openingCommissionPercent * (this.fundingSum) + this.openingCommissionConstant
        },
        xirrPayments(){
          const dataArray = [];

          const now = moment().set("date", 15);
          dataArray.push({
            when: now.clone().toDate(),
            amount: (this.fundingSum)
          });
          let currentDate = now.clone();
          for(let i = 0; i < this.calculator.payments; i++){
            currentDate = currentDate.add(1, 'months');
            let amount = this.getMonthlyPayment(false);
            amount = amount + this.collectionFee;

            if(i === 0){
              const grace = (this.calculatedBaseInterest / 365 * (currentDate.diff(now, 'days') - 30));
              amount = amount + (grace > 0 && grace < 1 ? (grace * this.fundingSum) : 0);
            }

            if(i < this.openingCommissionDivider){
              amount = amount + (this.openingCommission / this.openingCommissionDivider);
            }

            if(i > 12 && (i-1) % 12 === 0){
              amount = amount + this.extendingEncumbrance + this.profOfInsurance;
            }

            if((i+1) === this.calculator.payments){
              amount = amount + this.releaseOfEncumbrance + this.calculator.bullet_loan_amount;
            }

            dataArray.push({
              when: currentDate.toDate(),
              amount: -amount
            });
          }

          try{
            return xirr(dataArray);
          }catch (e) {
            return 0;
          }
        },
        currentUrl(){
          return window.location.href
        },
				bulletloanEnabled(){
					return this.interest && this.interest.max_payments_with_bullet_loan
				},
        ctaUrl(){
          if(this.cta.url){
            return this.cta.url
          }
          return '#'
        },
        minBulletLoan(){
          if(this.interest){
						if(this.bulletloanEnabled){
							if(this.interest.min_bullet_loan_by_payments){
								const mblbp = this.interest.min_bullet_loan_by_payments
								const mblbpf = mblbp[`${this.calculator.payments}`]
								if(mblbpf && typeof(mblbpf) === 'number'){
									return mblbpf * this.calculator.car_cost
								}
							}
							if(this.interest.min_bullet_loan){
								return this.interest.min_bullet_loan * this.calculator.car_cost
							}
						}
          }
          return 0
        },
        carTypesOptions(){
          return [
            {
              label: 'פרטי',
              value: 'private'
            },
            {
              label: 'מונית',
              value: 'cab'
            },
            {
              label: 'משאית',
              value: 'truck'
            },
          ]
        },
        carYears(){
            if(this.maxCarYear === this.minCarYear){
                return [this.maxCarYear]
            }
            return this.interests
                /*.filter(inte => {
                    if(this.calculator.car_age === 'new'){
                        return inte.new
                    }else{
                        return !inte.new
                    }
                })*/
                .map(inte => (inte.year)).filter((year,index, self) => {
                return self.indexOf(year) === index
            }).sort().reverse();
        },
        newCarYears(){
          if(this.maxCarYear === this.minCarYear){
            return [this.maxCarYear]
          }
          return this.interests.filter(inte => {
            return inte.new
          }).map(inte => (inte.year)).filter((year,index, self) => {
            return self.indexOf(year) === index
          }).sort().reverse();
        },
        usedCarYears(){
          if(this.maxCarYear === this.minCarYear){
            return [this.maxCarYear]
          }
          return this.interests.filter(inte => {
            return !inte.new
          }).map(inte => (inte.year)).filter((year,index, self) => {
            return self.indexOf(year) === index
          }).sort().reverse();
        },
        interest() {
            if (this.interests && Array.isArray(this.interests)) {
                return this.interests.find(interest => {
                    if(interest.year === this.calculator.car_year &&
                        ((this.calculator.car_age === 'new' && interest.new) || (this.calculator.car_age === 'used' && !interest.new))
                    ){
                        return true
                    }
                })
            }
            return null
        },
        maxPayments() {
            if(this.interest){
                return this.calculator.bullet_loan_amount && this.interest.max_payments_with_bullet_loan ? this.interest.max_payments_with_bullet_loan : this.interest.max_payments_without_bullet_loan_max_deviation
            }
            return 12
        },
        maxAvailablePayments(){
            const p = []
            if(this.interest.max_payments_without_bullet_loan){
                p.push(this.interest.max_payments_without_bullet_loan)
            }
            if(this.interest.max_payments_with_bullet_loan){
                p.push(this.interest.max_payments_with_bullet_loan)
            }
            if(this.interest.max_payments_without_bullet_loan_max_deviation){
                p.push(this.interest.max_payments_without_bullet_loan_max_deviation)
            }
            if(this.interest.max_payments_without_interest){
                p.push(this.interest.max_payments_without_interest)
            }
            return Math.max(...p)
        },
        availablePayments() {
            let p = []
            if (this.interest) {
                for (let i = 24; i <= this.maxAvailablePayments; i = i + 12) {
                    if(i === 96){
                        i = i + 4
                    }
                    p.push({value: i, disabled: i > this.maxPayments})
                }
            }
            return p
        },
        maxBulletLoanAmount() {
            const car_cost = this.calculator.car_cost
            const max_percent = this.interest.max_bullet_loan_percent_from_price_list_for_two_months
            const maxByInterest = car_cost * max_percent
            const maxByImprest = car_cost - this.calculator.imprest
            if (maxByImprest < maxByInterest && maxByImprest < car_cost && this.calculator.imprest + this.calculator.bullet_loan_amount <= car_cost) {
                return maxByImprest
            }
            return maxByInterest
        },
        minImprest() {
            if (this.calculator.car_cost - this.interest.max_loan > 0) {
                return this.calculator.car_cost - this.interest.max_loan
            }
            return 0
        },
        maxImprest() {
            if (this.minimum_loan < this.calculator.bullet_loan_amount) {
                return this.calculator.car_cost - this.calculator.bullet_loan_amount
            }
            return this.calculator.car_cost - this.minimum_loan
        },
        maxCarYear() {
            if (this.interests && this.interests.length > 0) {
                return Math.max(...this.interests.map(inte => (inte.year)))
            }
            return 0
        },
        minCarYear() {
            if (this.interests && this.interests.length > 0) {
                return Math.min(...this.interests
                    /*.filter(inte => {
                        if(this.calculator.car_age === 'new'){
                            return inte.new
                        }
                        return true
                    })*/
                    .map(inte => (inte.year)))
            }
            return 0
        },
        agency:{
            get(){
                return this.$store.getters.agency
            },
            set(value){
                this.$store.commit('agency', value)
            }
        },
        calculatedBaseInterest() {
            let interest = 0;

						if(this.interest){
							interest = this.interest.base_interest_percent
						}

            if (this.agency && !this.interest.agency_id) {
                const products = this.agency.products
                if (products) {
                    const dealType = this.calculator.car_age
                    const product = products.find(prod => prod.normalized_deal_type === dealType && prod.car_type === this.calculator.car_type)
                    if(product && product.static_interest_index_linked){
                        interest = product.static_interest_index_linked
                    }
                }
            }

            return interest + this.supplementaryInterest
        },
				supplementaryInterest(){
					const interestArr = [0];

					if(this.calculator && this.calculator.bullet_loan_amount){
						//interest = interest + this.interest.bullet_loan_interest_percent
						interestArr.push(this.interest.bullet_loan_interest_percent)
					}

					if(this.interest && this.calculator.payments > this.interest.max_payments_without_bullet_loan){
						//interest = interest + this.interest.payments_deviation_interest_percent
						interestArr.push(this.interest.payments_deviation_interest_percent)
					}

					if(this.calculator && this.calculator.imprest === 0 && this.interest && this.interest.no_imprest_interest_addition){
						//interest = interest + this.interest.no_imprest_interest_addition
						interestArr.push(this.interest.no_imprest_interest_addition)
					}
					return Math.max(...interestArr);
				},
				calculatedBaseInterestWithXirr(){
					if(this.xirrTest){
						return this.calculatedBaseInterest + this.primeInterest + this.primePlus
					}
					return this.calculatedBaseInterest;
				},
        currentAgencyId(){
          return (this.agency && this.agency.id) ? this.agency.id : ((this.interest && this.interest.agency_id) ? this.interest.agency_id : this.agencyId)
        },
				calculatorType: {
					get(){
						return this.$store.getters.calculatorType
					},
					set(value){
						this.$store.commit('calculatorType', value)
					}
				},
        calculatorTypeOptions(){
          return [
            {
              label: 'מימוני',
              value: 'funding'
            },
            {
              label: 'ליסינג',
              value: 'leasing'
            }
          ]
        },
				isFunding(){
					return this.calculatorType === 'funding'
				},
				calculator: {
					get(){
						return this.$store.getters.calculator
					},
					set(value){
						this.$store.commit('calculator', value)
					}
				},
				hasBulletLoanInterestPercent(){
					return this.interest && this.interest.bullet_loan_interest_percent
				},
				carTypeLabel(){
					if(this.calculator && this.calculator.car_type) {
						switch (this.calculator.car_type){
							case 'cab':
								return 'מונית'
							case 'truck':
								return 'משאית'
						}
					}
					return 'רכב'
				},
        carAgeOptions(){
          let newLabel = 'חדש';
          let usedLabel = 'משומש';
          if(this.calculator.car_type !== 'private') {
            newLabel = 'חדשה';
            usedLabel = 'משומשת';
          }
          return [
            {
              label: newLabel,
              value: 'new'
            },
            {
              label: usedLabel,
              value: 'used'
            }
          ]
        },
        newestYear(){
          return this.carYears[0];
        },
        ...mapState(['interests']),
        ...mapGetters([
          'cta',
          'defaultAge',
          'defaultYear',
          'defaultPresets',
          'hideAge',
          'hideYear',
          'hideType',
          'titleText',
          'bulletLoanLabel',
          'monthsLabel',
          'imprestLabel',
          'monthlyPaymentLabel',
          'fees',
					'token',
					'onlyCarType',
					'showOnlyResults',
					'defaultBulletLoan',
					'defaultImprest',
					'leasingCalculatorEnabled',
					'agencyId'
        ])
    },
    watch: {
        interests(){
					if(this.bulletloanEnabled){
						this.calculator.bullet_loan_amount = this.sliderBulletLoanAmount = this.minBulletLoan
					}else{
						this.calculator.bullet_loan_amount = this.sliderBulletLoanAmount = 0
					}
        },
        preferred_loan(newVal) {
            this.changePredefinedLoan(newVal)
        },
        monthlyPayment(newVal){
            if(newVal !== this.sliderMonthlyPayment){
                if(!isNaN(newVal)){
                    setTimeout(() => {
                      this.sliderMonthlyPayment = newVal
                    }, 500)
                }
            }
        },
				agency(newValue, oldValue){
					if(newValue !== oldValue){
						this.calcMonthlyPayment()
					}
				}
    },
    async beforeCreate() {

        await this.$store.dispatch('loadFees')
        if(this.fees){
          if(this.fees.benchmark !== undefined) this.benchmark = this.fees.benchmark
          if(this.fees.primeInterest !== undefined) this.primeInterest = this.fees.primeInterest
          if(this.fees.primePlus !== undefined) this.primePlus = this.fees.primePlus
          if(this.fees.openingCommissionPercent !== undefined) this.openingCommissionPercent = this.fees.openingCommissionPercent
          if(this.fees.openingCommissionConstant !== undefined) this.openingCommissionConstant = this.fees.openingCommissionConstant
          if(this.fees.collectionFee !== undefined) this.collectionFee = this.fees.collectionFee
          if(this.fees.releaseOfEncumbrance !== undefined) this.releaseOfEncumbrance = this.fees.releaseOfEncumbrance
          if(this.fees.extendingEncumbrance !== undefined) this.extendingEncumbrance = this.fees.extendingEncumbrance
          if(this.fees.profOfInsurance !== undefined) this.profOfInsurance = this.fees.profOfInsurance
          if(this.fees.openingCommissionDivider !== undefined) this.openingCommissionDivider = this.fees.openingCommissionDivider
					console.log({fees: JSON.parse(JSON.stringify(this.fees))})
        }

				const agencyId = localStorage.getItem('agencyId')
				if(agencyId){
					this.$store.commit('agencyId', agencyId)
				}

				await this.$store.dispatch('loadInterests')

        this.calculator.car_year = this.carYears[0]

        this.calculator.car_age = this.defaultAge

        let dy = this.defaultYear
        if(typeof dy === 'string'){
          dy = parseInt(dy)
        }
        const yearInNewCars = this.newCarYears.findIndex(cy => cy === dy) !== -1
        const yearInUsedCars = this.usedCarYears.findIndex(cy => cy === dy) !== -1
        if(dy && (yearInNewCars || yearInUsedCars)){
          if(yearInNewCars && !yearInUsedCars){
            this.calculator.car_age = 'new'
          }else if(!yearInNewCars && yearInUsedCars){
            this.calculator.car_age = 'used'
          }
          this.calculator.car_year = dy
        }


				if(this.defaultBulletLoan){
					if(this.hasBulletLoanInterestPercent){
						this.calculator.bullet_loan_amount = this.defaultBulletLoan
					}else{
						this.calculator.bullet_loan_amount = 0
					}
					this.sliderBulletLoanAmount = this.defaultBulletLoan
					this.bulletLoanAmountChanged()
				}

				if(this.defaultImprest){
					this.calculator.imprest = this.defaultImprest
					this.sliderImprest = this.defaultImprest
					this.sliderImprestChanged(this.defaultImprest)
				}

				if(this.showOnlyResults){
					this.stage = 2
				}

				if(!this.defaultBulletLoan && !this.defaultImprest){
					this.$nextTick(() => {
						this.changePredefinedLoan(this.defaultPresets);
					})
				}

    },
    methods: {
				changePredefinedLoan(preferredLoan){
					switch (preferredLoan) {
						case 'popular':
							this.calculator.imprest = 25000
							this.sliderImprest = 25000
							this.calculator.bullet_loan_amount = this.hasBulletLoanInterestPercent ? this.calculator.car_cost * 0.15 : 0
							this.sliderBulletLoanAmount = this.calculator.car_cost * 0.15
							this.calculator.payments = this.maxPayments
							break;
						case 'low_monthly_payment':
							this.calculator.imprest = this.maxImprest
							this.sliderImprest = this.maxImprest
							this.calculator.bullet_loan_amount = this.hasBulletLoanInterestPercent ? this.maxBulletLoanAmount : 0
							this.sliderBulletLoanAmount = this.maxBulletLoanAmount
							this.calculator.payments = this.maxPayments
							break;
						case 'no_imprest':
							this.calculator.imprest = 0
							this.sliderImprest = 0
							this.calculator.bullet_loan_amount = this.hasBulletLoanInterestPercent ? this.maxBulletLoanAmount : 0
							this.sliderBulletLoanAmount = this.maxBulletLoanAmount
							this.calculator.payments = this.maxPayments
							break;
					}
					this.calcMonthlyPayment()
				},
				calcMinMonthlyPayment(){
					if(this.interest){
						const yearlyMonths = 12
						const R = this.calculatedBaseInterestWithXirr // אחוז ריבית
						let N = this.calculator.payments // תשלומים
						const FV = this.calculator.bullet_loan_amount // הלוואה בסוף התקופה
						const DP = this.maxImprest // מקדמה
						const PV = this.calculator.car_cost - FV - DP // סכון ההלואה בניקוי הבלון והמקדמה
						if(!N){
							N = this.maxPayments
						}
						const result = Math.round((R / yearlyMonths / (1 - (1 / Math.pow(1 + R / yearlyMonths, N)))) * PV + (R / yearlyMonths) * FV)
						if(result >= 0){
							this.minMonthlyPayment = result
						}
					}else{
						this.minMonthlyPayment = this.calculator.car_cost / this.calculator.payments
					}
				},
				calcMaxMonthlyPayment(){
					if(this.interest){
						const yearlyMonths = 12
						let R = this.calculatedBaseInterestWithXirr // אחוז ריבית
						const N = this.calculator.payments // תשלומים
						const PV = this.calculator.car_cost - this.minBulletLoan // סכום ההלואה
						this.maxMonthlyPayment = Math.round((R / yearlyMonths / (1 - (1 / Math.pow(1 + R / yearlyMonths, N)))) * PV + (R / yearlyMonths) * this.minBulletLoan)
					}else{
						this.maxMonthlyPayment = this.calculator.car_cost / this.calculator.payments
					}
				},
        sliderImprestChanged(e){
            this.preferred_loan = 'custom'
            this.calculator.imprest = e
            this.monthlyPaymentSelfChange = false
            this.calcMonthlyPayment()
            setTimeout(() => {
                this.monthlyPaymentSelfChange = true
            })
        },
        sliderBulletLoanAmountChanged(e){
            if(e >= this.minBulletLoan){
              this.calculator.bullet_loan_amount = e
              this.bulletLoanAmountChanged()
            }
        },
        sliderMonthlyPaymentChanged(pmt){
            if(this.interest && this.monthlyPaymentSelfChange){
                const N = this.calculator.payments
                const R = this.calculatedBaseInterestWithXirr
                const TL = this.calculator.car_cost
                const AR = R/12
                const Z = (1-1 / Math.pow((1+AR),N))
                const BLA = this.calculator.bullet_loan_amount
                const IM = this.calculator.imprest

                const DP = Math.round(TL - BLA - ((pmt - AR * BLA)/(AR/Z)))
                const FV = Math.round((((AR / Z) * (TL - IM)) - pmt)/(AR / Z - AR))

                if(FV > 0 && FV <= this.maxBulletLoanAmount && FV >= this.minBulletLoan && N <= this.interest.max_payments_without_bullet_loan){
                    if(this.bulletloanEnabled){
											this.calculator.bullet_loan_amount = FV
											this.sliderBulletLoanAmount = FV
										}else{
											this.sliderBulletLoanAmount = 0
											this.calculator.bullet_loan_amount = 0
										}
                }else if((TL - DP) >= this.minimum_loan && DP >= 0 && DP < Infinity){
                    this.calculator.imprest = DP
                    this.sliderImprest = DP
                } else{
                    if(DP <= 0){
                        this.calculator.imprest = 0
                        this.sliderImprest = 0
                    }
                    if(FV <= 0){
												if(this.bulletloanEnabled){
													this.calculator.bullet_loan_amount = this.minBulletLoan
													this.sliderBulletLoanAmount = this.minBulletLoan
												}else{
													this.calculator.bullet_loan_amount = 0
													this.sliderBulletLoanAmount = 0
												}
                    }
                }
                this.preferred_loan = 'custom'
                this.updateAvailablePayment()
                setTimeout(() => {
									this.calcMonthlyPayment()
								})
            }
        },
				getMonthlyPayment(withXirr = true){
					if(this.interest){
						const yearlyMonths = 12
						const R = withXirr ? this.calculatedBaseInterestWithXirr : this.calculatedBaseInterest // אחוז ריבית
						// TODO: calculate RL & RP conditionally
						// TODO: add max & mix interest percent
						let N = this.calculator.payments // תשלומים
						const FV = this.calculator.bullet_loan_amount // הלוואה בסוף התקופה
						const DP = this.calculator.imprest // מקדמה
						const PV = this.calculator.car_cost - FV - DP // סכון ההלואה בניקוי הבלון והמקדמה
						//const NC = this.calculator.car_age === 'new'
						if(!N){
							N = this.maxPayments
						}
						const result = Math.round((R / yearlyMonths / (1 - (1 / Math.pow(1 + R / yearlyMonths, N)))) * PV + (R / yearlyMonths) * FV)
						if(result >= 0){
							return result
						}
					}
					return 0;
				},
        calcMonthlyPayment(){
						// this.minMonthlyPayment = this.minMonthlyPayment > 0 ? this.minMonthlyPayment / 2 : 0;
						// this.maxMonthlyPayment = this.maxMonthlyPayment > 0 ? this.maxMonthlyPayment * 2 : this.calculator.car_cost;
						this.monthlyPayment = this.getMonthlyPayment()
						this.calcMaxMonthlyPayment()
						this.calcMinMonthlyPayment()
        },
        bulletLoanAmountChanged() {
            this.preferred_loan = 'custom'
            this.monthlyPaymentSelfChange = false
            this.updateAvailablePayment()
            this.calcMonthlyPayment()
            setTimeout(() => {
                this.monthlyPaymentSelfChange = true
            })
        },
        updateAvailablePayment(){
            if(this.calculator.payments > this.maxPayments){
                this.calculator.payments = this.maxPayments
            }
        },
        paymentsChanged() {
            this.preferred_loan = 'custom'
            this.monthlyPaymentSelfChange = false
            this.calcMonthlyPayment()
						if(this.bulletloanEnabled){
							if(this.calculator.bullet_loan_amount < this.minBulletLoan && this.hasBulletLoanInterestPercent){
								this.calculator.bullet_loan_amount = this.sliderBulletLoanAmount = this.minBulletLoan
							}
						}else{
							this.calculator.bullet_loan_amount = this.sliderBulletLoanAmount = 0
						}
            setTimeout(() => {
                this.monthlyPaymentSelfChange = true
            })
        },
        nextStage() {
						if(this.calculatorType === 'leasing' && this.stage === 1){
							this.$router.push({name: 'leasing'})
							return;
						}
            this.stage++
            this.enforceImprestNotBelowMin()
            this.calcMonthlyPayment()
            const label = [
              `selected_preset:${this.preferred_loan}`,
              `car_year:${this.calculator.car_year}`,
              `car_type:${this.calculator.car_type}`,
              `car_age:${this.calculator.car_age}`,
              `car_cost:${this.calculator.car_cost}`,
              `monthly_payment:${this.monthlyPayment}`,
              `bullet_loan_interest_percent:${this.interest.bullet_loan_interest_percent}`,
              `base_interest_percent:${this.interest.base_interest_percent}`,
              `no_imprest_interest_addition:${this.interest.no_imprest_interest_addition}`,
              `payments_deviation_interest_percent:${this.interest.payments_deviation_interest_percent}`,
              `url:${this.currentUrl}`,
              `agency_id:${this.currentAgencyId}`,
              `xirr:${this.xirrPayments}`,
            ].join(',');
            this.reportEvent({action: 'continue button clicked', label})
        },
        async previousStage() {
            this.stage--
            const label = [
              `payments:${this.calculator.payments}`,
              `imprest:${this.calculator.imprest}`,
              `bullet_loan_amount:${this.calculator.bullet_loan_amount}`,
              `selected_preset:${this.preferred_loan}`,
              `car_year:${this.calculator.car_year}`,
							`car_type:${this.calculator.car_type}`,
              `car_age:${this.calculator.car_age}`,
              `car_cost:${this.calculator.car_cost}`,
              `monthly_payment:${this.monthlyPayment}`,
              `bullet_loan_interest_percent:${this.interest.bullet_loan_interest_percent}`,
              `base_interest_percent:${this.interest.base_interest_percent}`,
              `no_imprest_interest_addition:${this.interest.no_imprest_interest_addition}`,
              `payments_deviation_interest_percent:${this.interest.payments_deviation_interest_percent}`,
              `agency_id:${this.currentAgencyId}`,
              `url:${this.currentUrl}`,
              `xirr:${this.xirrPayments}`,
            ].join(',');
            await this.reportEvent({action: 'back button clicked', label})
        },
        numberFormat(number){
            return numeral(number).format("0,0")
        },
        async done(e) {
          if(this.ctaUrl === '#'){
            e.preventDefault()
            await this.previousStage()
          }else{
            const label = [
              `payments:${this.calculator.payments}`,
              `imprest:${this.calculator.imprest}`,
              `bullet_loan_amount:${this.calculator.bullet_loan_amount}`,
              `selected_preset:${this.preferred_loan}`,
              `car_year:${this.calculator.car_year}`,
							`car_type:${this.calculator.car_type}`,
              `car_age:${this.calculator.car_age}`,
              `car_cost:${this.calculator.car_cost}`,
              `monthly_payment:${this.monthlyPayment}`,
              `bullet_loan_interest_percent:${this.interest.bullet_loan_interest_percent}`,
              `base_interest_percent:${this.interest.base_interest_percent}`,
              `no_imprest_interest_addition:${this.interest.no_imprest_interest_addition}`,
              `payments_deviation_interest_percent:${this.interest.payments_deviation_interest_percent}`,
              `agency_id:${this.currentAgencyId}`,
              `url:${this.currentUrl}`,
              `xirr:${this.xirrPayments}`,
            ].join(',');
            await this.reportEvent({action: 'CTA button clicked', label})
          }
        },
        async logout(){
            await this.$store.dispatch('logout')
        },
        carAgeChanged(){
            if(this.carYears.findIndex(y => y === this.calculator.car_year) === -1){
                this.calculator.car_year = this.carYears[0]
            }
            if(this.calculator.car_age === 'used'){
								if(this.bulletloanEnabled){
									this.calculator.bullet_loan_amount = this.sliderBulletLoanAmount = this.minBulletLoan
								}else{
									this.calculator.bullet_loan_amount = this.sliderBulletLoanAmount = 0
								}
                if(this.minImprest > 0 && this.calculator.imprest < this.minImprest){
                    this.enforceImprestNotBelowMin()
                }else{
                    this.calculator.imprest = 0
                    this.sliderImprest = 0
                }
                this.sliderImprest = 0
            }
            this.updateAvailablePayment()
            this.calcMonthlyPayment()
        },
        carYearChanged(){
            if(this.calculator.car_year === this.newestYear){
                this.calculator.car_age = 'new'
            } else {
                this.calculator.car_age = 'used'
            }
            if(!this.bulletloanEnabled){
                if(this.minImprest !== 0){
                    this.preferred_loan = 'popular'
                    this.calculator.imprest = this.minImprest
                    this.sliderImprest = this.minImprest
                }else{
                    this.preferred_loan = 'no_imprest'
                }
            }else if(this.minImprest !== 0){
                this.preferred_loan = 'popular'
                this.calculator.imprest = this.minImprest
                this.sliderImprest = this.minImprest
            }else{
                this.preferred_loan = 'popular'
            }
            this.calcMonthlyPayment()
        },
        async carTypeChanged(){
					await this.$store.commit('carType', this.calculator.car_type);
          await this.$store.dispatch('loadInterests')
          this.calcMonthlyPayment()
        },
        carCostChanged(){
            this.enforceImprestNotBelowMin()
            this.calcMonthlyPayment()
        },
        enforceImprestNotBelowMin(){
            if(this.minImprest > 0 && this.calculator.imprest < this.minImprest){
                this.calculator.imprest = this.minImprest
                this.sliderImprest = this.minImprest
            }
        },
        yearsFromMonths(months){
            if(isNaN(months)){
                return 0
            }
            if(months === 1 || months === "1"){
                return '1 שנה'
            }
            return Math.floor(months / 12) + ' שנים'
        },
        ...mapActions(['reportEvent'])
    }
}
</script>

<style lang="scss">

    .shadow-calculator {
        display: block;
    }

    .calculator {

        container-name: calculator-container;
        container-type: inline-size;

        --slider-handle-width: 45px;
        --slider-handle-height: 45px;
        --slider-connect-bg: var(--primary-color);
        --slider-bg: var(--btn-bg-invert);
        --slider-handle-bg: white;
        --slider-handle-radius: 24px 0 24px 24px;
        --slider-handle-shadow: 0px 0px 25px 0px rgba(0, 0, 0, 0.60);
        --slider-height: 45px;
        --slider-radius: 24px 0 24px 24px;
        line-height: 1;
        color: var(--font-color);
        p {
            color: var(--font-color);
        }
        font-size: 16px;

        display: flex;
        justify-content: center;
        align-items: center;

        label {
            color: var(--font-color);
            font-size: 20px;
            font-weight: 400;
        }

        a {
            color: var(--font-color);
            font-weight: 700;
            &:hover,
            &:focus {
                color: #d0d0d0;
            }
        }

        img {
          max-width: 100%;
        }

        button {
            background-image: none;
            background-color: var(--btn-bg);
            border-radius: 5px;
            border: none;
            color: var(--btn-text);
            font-size: 1.125rem;
            min-width: 230px;
            padding: 5px;
            cursor: pointer;
        }

        h2.title {
          font-size: 24px;
          font-weight: 400;
          margin-top: 20px;
          margin-bottom: 20px;
          text-align: center;
        }

        .text-center {
            text-align: center;
        }

        .pm-input-100-w {
            width: 100%;
        }
        .pm-input {
            background: #FFFFFF;
            border: 1px solid #E7E7E7;
            box-sizing: border-box;
            border-radius: 24px 0 24px 24px;
            height: 45px;
            color: var(--input-font-color);
            min-width: 80px;
            padding: 4px 10px;
            font-size: 1.125rem;
            font-weight: 900;
            font-family: Rubik, sans-serif;
            option {
              font-size: 1.125rem;
              font-weight: 900;
              font-family: Rubik, sans-serif;
            }
        }

        select.pm-input {
          background-image: url("data:image/svg+xml,%3Csvg width='11' height='9' viewBox='0 0 11 9' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.5 9L0.736861 0.749999L10.2631 0.75L5.5 9Z' fill='%232E3172'/%3E%3C/svg%3E");
          background-position: left 10px center;
          background-repeat: no-repeat;
          appearance: none;
        }

        .bg-section {
            padding: 10px 20px 20px 20px;
            background-color: #fff;
            border-radius: 30px 0 30px 30px;
            .pm-input {
                background-color: #E9E9EB;
            }

            label {
              color: var(--primary-color);
            }
        }

        .pm-radio-group {
            display: flex;
            flex-flow: row wrap;
            border-radius: 24px 0 24px 24px;
            height: 45px;
            background-color: #E9E9EB;
            overflow: hidden;

            &.full-width {
                flex-grow: 1;

                label {
                    flex-grow: 1;
                    text-align: center;
                }
            }

            label {
                font-size: 1.125rem;
                cursor: pointer;
                user-select: none;

                input[type="radio"] {
                    display: none;
                }

                > span {
                    padding: 15px 28px;
                    color: var(--btn-text-invert);
                    border-radius: 24px 0 24px 24px;
                    display: block;
                }

                > input[type="radio"]:checked + span {
                    background-color: var(--primary-color);
                    color: var(--btn-text);
                }

                > input[disabled] + span {
                    /*background-color: #737373;*/
										/*color: var(--btn-text);*/
                    filter: blur(1px);
                    opacity: 0.6;
                    cursor: not-allowed;
                }
            }
        }


        .form-group {
            display: flex;
            flex-flow: column nowrap;

            > label {
                margin-bottom: 8px;
                font-size: 1.125rem;
            }

            .flex label,
            .range-wrap label {
                width: 100px;
            }
        }

        .flex {
            display: flex;
            flex-flow: row wrap;
            justify-content: flex-start;
        }

        .flex-wrap {
            flex-wrap: wrap;
        }

        .flex-nowrap {
            flex-wrap: nowrap;
        }

        .flex-column {
            flex-direction: column;
        }

        .flex-row {
            flex-direction: row;
        }

        .flex-grow-1 {
            flex-grow: 1;
        }

        .flex-grow-0 {
            flex-grow: 0;
        }

        .flex-shrink-1 {
            flex-shrink: 1;
        }

        .flex-shrink-0 {
            flex-shrink: 0;
        }

        .justify-content-center {
            justify-content: center;
        }

        .align-items-center {
            align-items: center;
        }

        .gap-40 {
            gap: 2.5rem;
        }

        .gap-30 {
            gap: 1.875rem;
        }

        .mb-20 {
            margin-bottom: 20px;
        }

        .section {
            margin-bottom: 20px;
        }

        .monthly-payment-wrapper {
          margin-inline-start: 100px;

          img {
            width: 340px;
          }

          .image-mobile {
            display: none;
          }
        }

        .monthly-payment {
            max-width: 100%;
            margin-top: auto;
            margin-bottom: auto;
            padding: 0 60px;
            box-sizing: border-box;
            position: relative;
            text-align: center;
            color: var(--secondary-color);
            font-size: 1.25rem;

            .range-slider {
              margin-top: 15px;
              .slider-connect {
                background-color: var(--secondary-color);
              }
            }

            .pm-input {
                text-align: right;
                color: var(--font-color);
                background-color: transparent;
                border: none;
                box-shadow: none;
                font-size: 3rem;
                font-weight: 700;
                white-space: nowrap;
                margin-top: 15px;

                &:focus {
                    outline: none;
                }
            }

            .price-view {
              color: var(--secondary-color);
              margin-bottom: 15px;
            }
        }

        .range-wrap {
            display: flex;
            flex-flow: row nowrap;
            align-items: center;
            gap: 1.875rem;

            .range-slider {
                flex-grow: 1;
            }
        }

        .slider-tooltip {
            background-color: transparent;
            border: none;
            display: none;
        }

        .slider-handle {
          background-size: 100%;
        }

        .slider-handle.slider-active .slider-tooltip{
            display: block;
        }

        .price-view {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 4px 8px;
        }

        .cta-container {
            margin-top: 100px;
            text-align: center;

            .cta-btn {
                text-decoration: none;
                background-color: var(--btn-bg);
                padding: 10px 25px;
                border-radius: 5px;
            }
        }
    }

    @container calculator-container (width > 1188px) {
      .calculator {
        padding-top: 120px;
      }

      .hidden-xl {
        display: none !important;
      }
    }

    @container calculator-container (width < 1188px) {
      .calculator {
        padding-right: 15px;
        padding-left: 15px;
        display: flex;
        flex-flow: column;
        align-items: flex-start;

        .flex-column-sm {
          flex-direction: column;
        }

        .half-mobile {
          width: 50%;
        }

        .align-items-start-sm {
          align-items: flex-start;
        }

        .hidden-sm {
          display: none !important;
        }

        .pm-radio-group {

          label {
            font-size: 1rem;
            width: auto !important;
          }
        }

        button {
          padding: 20px 5px;
        }

        .section {
          max-width: calc(100vw - 30px);
        }

        .monthly-payment-wrapper {
          margin-inline-start: 0;
          margin-top: 20px;
          margin-bottom: 20px;
          display: flex;
          flex-flow: column-reverse;
          gap: 40px;
          align-items: center;

          .image-mobile {
            display: block;
          }

          .image-desktop {
            display: none;
          }
        }

        .monthly-payment {
          width: 100%;
          padding: 0 15px;
        }

        .range-wrap {
          flex-wrap: wrap;

          label {
            flex-basis: 100%;
            margin-bottom: -20px;
          }
        }
      }
    }

		.light-theme .calculator .pm-radio-group label > span {
			border: solid 1px #C6C6C6;
		}
</style>
